<template>
  <div class="search-page">
    <header class="topbar">
      <button
        class="back-btn"
        @click="$router.back()"
      >
        <span class="chev">‹</span> Anterior
      </button>

      <div
        class="searchbar"
        :class="{ focused: isFocused }"
      >
        <span class="icon">🔍</span>
        <input
          ref="input"
          v-model="query"
          type="text"
          :placeholder="placeholder"
          :disabled="loading"
          :aria-busy="loading ? 'true' : 'false'"
          @focus="isFocused = true"
          @blur="isFocused = false"
          @keyup.enter="onEnter"
        >
        <button
          v-if="query"
          class="clear"
          :disabled="loading"
          @click="clearQuery"
        >
          ×
        </button>
      </div>
    </header>

    <!-- feedback global de busca -->
    <div
      v-if="state === 'results' && loading"
      class="search-feedback"
      role="status"
      aria-live="polite"
    >
      <span
        class="spinner"
        aria-hidden="true"
      />
      <span>Buscando…</span>
    </div>

    <!-- estado vazio -->
    <section
      v-if="state === 'idle'"
      class="empty"
    >
      <div class="big-icon">
        🔎
      </div>
      <p class="hint">
        Procurar por ID de conversa, e-mail, número de telefone, mensagens para melhores
        resultados de busca.
      </p>
    </section>

    <!-- resultados -->
    <section
      v-else
      class="results"
    >
      <nav class="tabs">
        <button
          v-for="t in tabs"
          :key="t.key"
          :class="['tab', { active: activeTab === t.key }]"
          @click="activeTab = t.key"
        >
          {{ t.label }}
          <span
            v-if="t.count !== null"
            class="badge"
          >{{ t.count }}</span>
        </button>
      </nav>

      <!-- Contato -->
      <div
        v-if="activeTab === 'contacts' || activeTab === 'all'"
        class="group"
      >
        <h3>Contato</h3>
        <div
          v-if="counts.contacts === 0"
          class="no-item"
        >
          Não foi encontrado contato para a consulta '{{ query }}'
        </div>
        <ul
          v-else
          class="message-list"
        >
          <li
            v-for="c in data.contacts"
            :key="c.id"
            class="message clickable"
            @click="abrirHistoricoContato(c)"
          >
            <div class="left-pill">
              👤
            </div>
            <div class="content">
              <div class="meta">
                <span class="tag">#{{ c.id }}</span>
                <span
                  v-if="c.phone"
                  class="chip"
                >{{ c.phone }}</span>
                <span
                  v-if="c.atendimentos && c.atendimentos.length > 0"
                  class="chip-attendances"
                >
                  {{ c.atendimentos.length }} atendimento(s)
                </span>
              </div>
              <div class="author">
                <strong>{{ c.name || '—' }}</strong>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Conversas -->
      <div
        v-if="activeTab === 'conversations' || activeTab === 'all'"
        class="group"
      >
        <h3>Conversas</h3>
        <div
          v-if="counts.conversations === 0"
          class="no-item"
        >
          Nenhuma conversa encontrada
        </div>
        <ul
          v-else
          class="message-list"
        >
          <li
            v-for="c in data.conversations"
            :key="c.id"
            class="message clickable"
            @click="abrirHistoricoConversa(c)"
          >
            <div class="left-pill">
              🗂️
            </div>
            <div class="content">
              <div class="meta">
                <!-- id + status ao lado -->
                <span class="tag">#{{ c.id }}</span>
                <span :class="['chip', c.status === 'em_andamento' ? 'chip-open' : 'chip-closed']">
                  {{ c.status === 'em_andamento' ? 'em andamento' : 'encerrado' }}
                </span>
                <span class="age">{{ timeAgo(c.lastMessageAt) }}</span>
              </div>
              <div class="author">
                <strong>{{ c.title }}</strong>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Mensagens -->
      <div
        v-if="activeTab === 'messages' || activeTab === 'all'"
        class="group"
      >
        <h3>Mensagens</h3>

        <div
          v-if="loading"
          class="loading"
        >
          Carregando…
        </div>

        <div
          v-else-if="counts.messages === 0"
          class="no-item"
        >
          Nenhuma mensagem encontrada
        </div>

        <ul
          v-else
          class="message-list"
        >
          <li
            v-for="m in data.messages"
            :key="m.id"
            class="message clickable"
            @click="abrirHistoricoMensagem(m)"
          >
            <div class="left-pill">
              💬
            </div>
            <div class="content">
              <div class="meta">
                <span class="tag">#{{ m.id }}</span>
                <span class="chip">{{ m.channel }}</span>
                <span class="age">{{ m.age }}</span>
              </div>
              <div class="author">
                <strong>{{ m.author }}</strong> escreveu —
                <em>atendimento de {{ m.clienteNome || 'Cliente' }}</em>
              </div>
              <blockquote
                class="body"
                v-html="formatBody(m.body)"
              />
            </div>
          </li>
        </ul>
      </div>
    </section>

    <!-- MODAL DE HISTÓRICO -->
    <ModalHistoricoAtendimento
      :visivel.sync="modalHistoricoVisivel"
      :historico="atendimentoSelecionado"
      @abrir-conversa-existente="abrirConversaExistente"
      @iniciar-conversa="iniciarConversaComContato"
    />

    <!-- Modal Nova Conversa em nível de Search (fora do modal de histórico) -->
    <NovaConversaModal
      ref="novaConversaModal"
      :conversas="data.conversations"
      @abrir-conversa-existente="abrirConversaExistente"
    />
    <!-- feedback dentro do modal (overlay leve) -->
    <div
      v-if="modalLoading"
      class="modal-loading-overlay"
    >
      <div class="modal-loading">
        <span
          class="spinner"
          aria-hidden="true"
        />
        <span>Carregando histórico…</span>
      </div>
    </div>

    <!-- toast simples de erro -->
    <div
      v-if="modalErro"
      class="toast-error"
    >
      {{ modalErro }}
    </div>
  </div>
</template>

<script>
import { search } from './search.adapter'
import { getChatOfContact } from '../../functions/basics/chat'
import ModalHistoricoAtendimento from './ModalHistoricoAtendimento'
import NovaConversaModal from './NovaConversaModal.vue'

export default {
  name: 'SearchView',
  components: { ModalHistoricoAtendimento, NovaConversaModal },
  data() {
    return {
      query: '',
      isFocused: false,
      state: 'idle',
      loading: false,
      activeTab: 'all',
      data: { contacts: [], conversations: [], messages: [] },
      debounceTimer: null,
      currentReqToken: 0, // controla respostas atrasadas
      // Modal
      modalHistoricoVisivel: false,
      atendimentoSelecionado: null, // { mensagens: [...] }
      modalLoading: false,
      modalErro: '',
    }
  },
  computed: {
    placeholder() {
      return 'Digite 3 ou mais caracteres para pesquisar'
    },
    counts() {
      return {
        contacts: this.data.contacts.length,
        conversations: this.data.conversations.length,
        messages: this.data.messages.length,
      }
    },
    tabs() {
      return [
        { key: 'all', label: 'Todos', count: this.totalCount },
        { key: 'contacts', label: 'Contato', count: this.counts.contacts },
        { key: 'conversations', label: 'Conversas', count: this.counts.conversations },
        { key: 'messages', label: 'Mensagens', count: this.counts.messages },
      ]
    },
    totalCount() {
      const c = this.counts
      return c.contacts + c.conversations + c.messages
    },
  },
  watch: {
    // Debounce da busca: só dispara quando o usuário para de digitar
    query(newVal) {
      clearTimeout(this.debounceTimer)
      if (!newVal) {
        this.state = 'idle'
        this.data = { contacts: [], conversations: [], messages: [] }
        return
      }
      this.debounceTimer = setTimeout(() => {
        this.triggerSearch()
      }, 600)
    },
  },
  methods: {
    onEnter() {
      // Enter dispara a busca imediatamente e cancela o debounce pendente
      clearTimeout(this.debounceTimer)
      this.triggerSearch()
    },
    clearQuery() {
      clearTimeout(this.debounceTimer)
      this.query = ''
      this.activeTab = 'all'
      this.state = 'idle'
      this.loading = false
      this.data = { contacts: [], conversations: [], messages: [] }
    },
    async triggerSearch() {
      if (!this.query || this.query.length < 3) return

      this.state = 'results'
      this.loading = true

      // token para ignorar respostas atrasadas
      const myToken = ++this.currentReqToken

      try {
        const res = await search(this.$route.params.uuid, this.query)
        if (myToken === this.currentReqToken) {
          this.data = res
        }
      } finally {
        if (myToken === this.currentReqToken) {
          this.loading = false
        }
      }
    },

    // ---- Modal: abrir histórico do contato
    abrirHistoricoContato(contato) {
      if (!contato || !contato.atendimentos || contato.atendimentos.length === 0) {
        this.$toast.info('Este contato não possui atendimentos.')
        return
      }

      // Preparar dados do contato para o modal
      const dadosContato = {
        contato: {
          id: contato.id,
          name: contato.name,
          phone: contato.phone,
        },
        atendimentos: contato.atendimentos,
        mensagens: [], // Será carregado quando selecionar um atendimento
      }

      this.atendimentoSelecionado = dadosContato
      this.modalHistoricoVisivel = true
    },

    // ---- Modal: abrir por conversa
    async abrirHistoricoConversa(c) {
      if (!c || !c.id) return
      await this._abrirHistoricoPorAtendimentoId(c.id)
    },

    // ---- Abrir NovaConversaModal com contato vindo do histórico
    iniciarConversaComContato(contato) {
      if (this.$refs && this.$refs.novaConversaModal && this.$refs.novaConversaModal.abrirComContato) {
        this.$refs.novaConversaModal.abrirComContato({
          id: contato && contato.id,
          name: contato && contato.name,
          phone: contato && contato.phone,
        })
      }
    },

    // ---- Abrir conversa existente
    abrirConversaExistente(conversa) {
      // Navegar para a conversa existente
      if (conversa && conversa.id) {
        this.$router.push(`/chat/${this.$route.params.uuid}/atendimento/${conversa.id}`)
      }
    },

    // ---- Modal: abrir por mensagem
    async abrirHistoricoMensagem(m) {
      const atendimentoId = m && m.atendimentoId
      if (!atendimentoId) return
      await this._abrirHistoricoPorAtendimentoId(atendimentoId)
    },

    // Core de abertura do histórico
    async _abrirHistoricoPorAtendimentoId(atendimentoId) {
      try {
        this.modalErro = ''
        this.modalLoading = true
        // pega as mensagens do atendimento
        const dados = await getChatOfContact(atendimentoId)
        // API pode retornar objeto { mensagens, historico_atendentes } ou array direto
        this.atendimentoSelecionado = { 
          mensagens: dados.mensagens || dados || [],
          historicoAtendentes: dados.historico_atendentes || [],
          status: dados.status || 'resolvido',
          fim: dados.fim || null,
        }
        this.modalHistoricoVisivel = true
      } catch (err) {
        console.error('Erro ao carregar histórico:', err)
        this.modalErro = 'Não foi possível carregar o histórico deste atendimento.'
      } finally {
        this.modalLoading = false
      }
    },

    formatBody(txt) {
      const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return esc(txt)
        .replace(/#([a-zA-Z_á-úÁ-Ú]+)/g, '<span class="token">#$1</span>')
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>')
    },
    timeAgo(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const diff = Date.now() - d.getTime()
      const m = Math.floor(diff / 60000)
      const h = Math.floor(m / 60)
      const dys = Math.floor(h / 24)
      if (dys) return `${dys} dia${dys > 1 ? 's' : ''} atrás`
      if (h) return `${h} hora${h > 1 ? 's' : ''} atrás`
      if (m) return `${m} minuto${m > 1 ? 's' : ''} atrás`
      return 'agora mesmo'
    },
  },
}
</script>

  <style scoped>
  /* ============================= */
  /* Light (padrão)                */
  /* ============================= */
  .search-page {
    --bg: #ffffff;
    --bg-elev: #f9fafb;
    --surface: #ffffff;
    --muted: #475569;
    --text: #0f172a;
    --accent: #1677ff;
    --border: #d1d5db;
    --badge: #e5e7eb;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
  }

  /* ============================= */
  /* Dark (quando body.dark-layout)*/
  body.dark-layout .search-page {
    --bg: #293046;
    --bg-elev: #313852;
    --surface: #293046;
    --muted: #cbd5e1;
    --text: #f1f5f9;
    --accent: #1677ff;
    --border: #3b425c;
    --badge: #3b425c;
    background: var(--bg);
    color: var(--text);
  }

  .topbar {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 16px;
  }
  .back-btn {
    background: #0c2b52;
    color: #fff;
    border: 0;
    border-radius: 8px;
    padding: 10px 14px;
    cursor: pointer;
  }
  .back-btn .chev { font-size: 18px; margin-right: 6px; }

  .searchbar {
    flex: 1;
    display: flex;
    align-items: center;
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: 8px;
    padding: 10px 12px;
    position: relative;
  }
  .searchbar.focused {
    border-color: var(--accent);
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.2) inset;
  }
  .searchbar .icon { margin-right: 8px; opacity: 0.9; }
  .searchbar input {
    flex: 1;
    background: transparent;
    border: 0;
    outline: none;
    color: var(--text);
    font-size: 18px;
  }
  .clear {
    position: absolute;
    right: 8px; top: 8px; bottom: 8px; margin: auto;
    height: 28px; width: 28px; border-radius: 6px;
    border: 0; background: var(--badge); color: var(--muted); cursor: pointer;
  }

  /* Feedback de busca (abaixo da barra) */
  .search-feedback {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 16px; color: var(--muted);
  }
  .spinner {
    width: 16px; height: 16px;
    border: 2px solid var(--badge);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.9s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .empty { text-align: center; margin-top: 16px; color: var(--muted); }
  .big-icon { font-size: 40px; margin-bottom: 20px; }
  .hint { max-width: 740px; margin: 0 auto; line-height: 1.6; }

  .results { padding: 0 16px 64px; }
  .tabs { display: flex; gap: 16px; border-bottom: 1px solid var(--border); }
  .tab { padding: 14px 8px; background: transparent; border: 0; color: var(--muted); cursor: pointer; }
  .tab.active { color: var(--text); border-bottom: 2px solid var(--accent); }
  .badge { margin-left: 6px; background: var(--badge); padding: 2px 6px; border-radius: 10px; font-size: 12px; color: var(--muted); }

  .group { margin-top: 18px; }
  .group h3 { font-weight: 600; color: var(--muted); margin: 10px 0 8px; }
  .no-item {
    background: var(--bg-elev);
    border: 1px dashed var(--border);
    color: var(--muted);
    padding: 16px; border-radius: 8px;
  }
  .loading { padding: 12px; color: var(--muted); }

  .message-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 14px; }
  .message { display: flex; gap: 10px; }
  .left-pill {
    width: 36px; height: 36px; background: var(--badge);
    border-radius: 8px; display: flex; align-items: center; justify-content: center;
  }
  .content { flex: 1; border-left: 2px solid var(--border); padding-left: 10px; }
  .meta { display: flex; gap: 10px; align-items: center; color: var(--muted); font-size: 12px; }
  .tag, .chip { background: var(--badge); padding: 2px 6px; border-radius: 6px; }
  .chip-attendances { background: var(--accent); color: white; font-weight: 500; }
  .age { margin-left: auto; }
  .author { margin: 6px 0; }
  .body { margin: 0; color: var(--text); }
  .body a { color: var(--accent); text-decoration: none; }
  .token { color: var(--accent); }

  /* Interação de clique */
  .clickable { cursor: pointer; transition: background 0.12s ease-in-out; }
  .clickable:hover { background: var(--bg-elev); border-radius: 8px; }

  /* Overlay de loading do modal */
  .modal-loading-overlay {
    position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
    background: rgba(0,0,0,0.15); z-index: 50;
  }
  .modal-loading {
    display: flex; gap: 10px; align-items: center;
    background: var(--surface); padding: 10px 14px; border-radius: 8px;
    color: var(--muted); border: 1px solid var(--border);
  }

  /* Status chips opcionais */
  .chip-open  { background: #dcfce7; color: #166534; }
  .chip-closed{ background: #fee2e2; color: #991b1b; }

  /* Toast simples de erro */
  .toast-error {
    position: fixed; bottom: 20px; right: 20px;
    background: #fee2e2; color: #991b1b; border: 1px solid #fecaca;
    padding: 10px 12px; border-radius: 8px; z-index: 60;
  }
  </style>
